import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Search } from 'lucide-react';
import { productService } from '../features/product/productService';
import { Game } from '../features/product/types';
import { cheapestRegionalOffer, hasActiveDiscount, originalPrice } from '../features/product/discount';

const fmt = (price: number) => new Intl.NumberFormat('ru-RU').format(price);

const SkeletonCard = () => (
  <div className="animate-pulse overflow-hidden rounded-[22px] border border-foreground/10 bg-foreground/5">
    <div className="aspect-[5/6] bg-foreground/5" />
    <div className="p-3">
      <div className="mb-1.5 h-4 w-16 rounded bg-foreground/5" />
      <div className="h-3.5 rounded bg-foreground/5" />
      <div className="mt-1 h-3.5 w-2/3 rounded bg-foreground/5" />
    </div>
  </div>
);

const ResultCard = ({ game, onClose }: { game: Game; onClose: () => void }) => {
  const offer = cheapestRegionalOffer(game);
  const priceSource = offer ?? game;
  const discount = hasActiveDiscount(priceSource) ? priceSource.discountPercent : null;
  const oldPrice = originalPrice(priceSource);
  return (
  <Link
    to={`/product/${game.id}`}
    onClick={onClose}
    className="group overflow-hidden rounded-[22px] border border-foreground/10 bg-card transition-all duration-200 hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-[0_12px_30px_rgba(0,0,0,0.4)]"
  >
    <div className="relative aspect-[5/6] overflow-hidden">
      <img
        src={game.picture}
        alt={game.name}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
      {game.brandNew && (
        <span className="absolute left-2.5 top-2.5 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          New
        </span>
      )}
      {game.preOrder && (
        <span className="absolute right-2.5 top-2.5 rounded-full bg-red-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          Pre
        </span>
      )}
      {discount && (
        <span className="absolute bottom-2.5 right-2.5 rounded-full border border-red-300/40 bg-gradient-to-r from-red-700 to-red-500 px-2.5 py-1 text-xs font-bold text-white shadow-[0_6px_18px_rgba(220,38,38,0.45)]">
          −{discount}%
        </span>
      )}
    </div>
    <div className="p-3">
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <div className="text-base font-semibold text-foreground">{fmt(game.price)} ₽</div>
        {oldPrice && (
          <div className="text-[11px] text-foreground/40 line-through decoration-red-500/70">{fmt(oldPrice)} ₽</div>
        )}
      </div>
      <h3 className="mt-1 line-clamp-2 text-xs font-medium leading-4 text-foreground/80">
        {game.name}
      </h3>
    </div>
  </Link>
  );
};

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

const SearchModal = ({ open, onClose }: SearchModalProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<Game[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const recsLoadedRef = useRef(false);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) {
      setGames([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      const res = await productService.fetchProducts({ name: q.trim(), pageSize: 24 });
      setGames(res.products as Game[]);
      setTotal(res.total);
    } catch {
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загружаем рекомендации один раз при первом открытии
  useEffect(() => {
    if (!open || recsLoadedRef.current) return;
    recsLoadedRef.current = true;
    productService.fetchProducts({ salesLeader: true, pageSize: 12 })
      .then(res => setRecommendations(res.products as Game[]))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Animation state: mount first, then trigger transition
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    if (open) {
      // Mount → next frame → show (triggers CSS transition)
      const raf = requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 80);
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      document.body.style.overflow = '';
      // Clear state after transition ends
      const t = setTimeout(() => { setQuery(''); setGames([]); }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open && !visible) return null;

  const showResults = !loading && query.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ transition: 'none' }}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Закрыть поиск"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{
          background: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 220ms ease',
        }}
      />

      {/* Panel */}
      <div
        className="relative z-10 mx-auto mt-16 flex w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-foreground/10 bg-card shadow-[0_32px_80px_rgba(0,0,0,0.6)]"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(-18px) scale(0.97)',
          transition: 'opacity 240ms cubic-bezier(0.22,1,0.36,1), transform 240ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* Search input row */}
        <div className="flex items-center gap-3 border-b border-foreground/10 px-5 py-4">
          <Search className="h-5 w-5 shrink-0 text-foreground/35" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && query.trim()) {
                onClose();
                navigate(`/catalog?name=${encodeURIComponent(query.trim())}`);
              }
            }}
            placeholder="Найти игру или подписку…"
            className="min-w-0 flex-1 bg-transparent text-base text-foreground placeholder-foreground/30 outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="shrink-0 text-foreground/30 transition hover:text-foreground/60"
              aria-label="Очистить"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-foreground/10 text-foreground/40 transition hover:border-foreground/20 hover:bg-foreground/5 hover:text-foreground"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results area */}
        <div className="max-h-[70vh] overflow-y-auto p-5">

          {/* Loading */}
          {loading && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {[...Array(12)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* Рекомендации (пустой запрос) */}
          {!loading && !query.trim() && (
            recommendations.length > 0 ? (
              <>
                <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-foreground/30">
                  Популярное
                </p>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {recommendations.map(game => (
                    <ResultCard key={game.id} game={game} onClose={onClose} />
                  ))}
                </div>
              </>
            ) : (
              <div className="py-12 text-center text-sm text-foreground/30">
                Начните вводить название
              </div>
            )
          )}

          {/* No results */}
          {showResults && games.length === 0 && (
            <div className="py-12 text-center text-sm text-foreground/30">
              Ничего не найдено по запросу «{query}»
            </div>
          )}

          {/* Results */}
          {showResults && games.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-xs text-foreground/30">Найдено: {total}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                {games.map(game => (
                  <ResultCard key={game.id} game={game} onClose={onClose} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
